import * as React from 'react';
import { cn } from '@/lib/utils';
import { CDPageContainer } from '../primitives/CDPageContainer';
import {
  type AdjustableInterestRateData,
  type AdjustablePaymentData,
  CDAdjustableInterestRateTable,
  CDAdjustablePaymentTable,
} from '../sections/CDAdjustableTables';
import { CDEscrowAccount, type EscrowAccountData } from '../sections/CDEscrowAccount';
import { CDLoanDisclosures, type LoanDisclosuresData } from '../sections/CDLoanDisclosures';

export interface CDPage4Data {
  loanDisclosures: Partial<LoanDisclosuresData>;
  escrowAccount: Partial<EscrowAccountData>;
  adjustablePayment?: Partial<AdjustablePaymentData>;
  adjustableInterestRate?: Partial<AdjustableInterestRateData>;
}

export interface CDPage4Props {
  /** Page 4 data */
  data: Partial<CDPage4Data>;
  /** Total number of pages */
  totalPages?: number;
  /** Form ID for footer */
  formId?: string;
  /** Whether fields are editable */
  editable?: boolean;
  /** Whether to show AP table */
  showAdjustablePayment?: boolean;
  /** Whether to show AIR table */
  showAdjustableInterestRate?: boolean;
  /** onChange handler for data updates */
  onChange?: (data: Partial<CDPage4Data>) => void;
  /** Additional className */
  className?: string;
}

/**
 * CDPage4 - Complete Page 4 of Closing Disclosure
 *
 * Contains:
 * - Loan Disclosures
 * - Escrow Account details
 * - Adjustable Payment (AP) Table (if applicable)
 * - Adjustable Interest Rate (AIR) Table (if applicable)
 * - Page footer
 */
export const CDPage4 = React.forwardRef<HTMLDivElement, CDPage4Props>(
  (
    {
      data,
      totalPages = 5,
      formId,
      editable = false,
      showAdjustablePayment = false,
      showAdjustableInterestRate = false,
      onChange,
      className,
    },
    ref,
  ) => {
    const handleLoanDisclosuresChange = (loanDisclosures: Partial<LoanDisclosuresData>) => {
      onChange?.({ ...data, loanDisclosures });
    };

    const handleEscrowAccountChange = (escrowAccount: Partial<EscrowAccountData>) => {
      onChange?.({ ...data, escrowAccount });
    };

    const handleAdjustablePaymentChange = (adjustablePayment: Partial<AdjustablePaymentData>) => {
      onChange?.({ ...data, adjustablePayment });
    };

    const handleAdjustableInterestRateChange = (
      adjustableInterestRate: Partial<AdjustableInterestRateData>,
    ) => {
      onChange?.({ ...data, adjustableInterestRate });
    };

    return (
      <CDPageContainer
        ref={ref}
        pageNumber={4}
        totalPages={totalPages}
        formId={formId}
        className={cn(className)}
      >
        {/* Loan Disclosures */}
        <CDLoanDisclosures
          data={data.loanDisclosures || {}}
          editable={editable}
          onChange={handleLoanDisclosuresChange}
        />

        {/* Escrow Account */}
        <div className="mt-3">
          <CDEscrowAccount
            data={data.escrowAccount || {}}
            editable={editable}
            onChange={handleEscrowAccountChange}
          />
        </div>

        {/* Adjustable Payment Table (if applicable) */}
        {showAdjustablePayment && data.adjustablePayment && (
          <div className="mt-3">
            <CDAdjustablePaymentTable
              data={data.adjustablePayment}
              editable={editable}
              onChange={handleAdjustablePaymentChange}
            />
          </div>
        )}

        {/* Adjustable Interest Rate Table (if applicable) */}
        {showAdjustableInterestRate && data.adjustableInterestRate && (
          <div className="mt-3">
            <CDAdjustableInterestRateTable
              data={data.adjustableInterestRate}
              editable={editable}
              onChange={handleAdjustableInterestRateChange}
            />
          </div>
        )}
      </CDPageContainer>
    );
  },
);

CDPage4.displayName = 'CDPage4';
